import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {CellReference} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {FormulaType} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaType';
import {OhmParseResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {SheetRangeImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeImpl';
import {SheetRangeIteratorImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeIteratorImpl';
import {IFormulaParseContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/buildAST/BuildFormulaAST';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {EvalStack} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalStack';
import {IFunctionResolver} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolver';
import {ISheetIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContext';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {SlotDependencyType, SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {
    ColumnIndex,
    RowIndex,
    SlotLocation
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {ViewportUtil} from '@mintdata-wsp/common/util/ViewportUtil';
import {ISheetCellFormat, ISheetCellSettings} from '@mintdata/mint-kit/api/functions/sheet/SheetCellSettings';
import {
    FormulaKind,
    IFormulaExpression,
    IsCellReEvaluationDone,
    ISheetContext,
    ISheetLocation,
    ISheetRange,
    ISheetRangeIterator,
    ISheetValue,
    ISurfaceContext,
    NamedArguments,
    SheetDirection,
    SheetLocationKey,
    SheetRangeSide,
    SheetType,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Empty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {AFrameColumn} from '@mintdata/mint-kit/api/values/frames/AFrameColumn';
import {FrameCellCoordinate, IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';
import _range from 'lodash/range';
import _times from 'lodash/times';

const AUTO_COLUMN_NAME_RE = /^column\d+$/;

type FrameOp = (frame:IFrame) => IFrame | AError;

class BindingOpResult
{
    public static SKIP = new BindingOpResult(null);
    public static DELETE = new BindingOpResult(null);

    constructor(public readonly newPathInFrameHead:FrameCellCoordinate)
    {
    }

    public static change(newPathInFrameHead:FrameCellCoordinate):BindingOpResult
    {
        return new BindingOpResult(newPathInFrameHead);
    }
}

type BindingOp = (oldFrame:IFrame, newFrame:IFrame, pathInFrameHead:FrameCellCoordinate) => BindingOpResult;

export class FormulaExpressionImpl implements IFormulaExpression
{
    public constructor(private readonly _invoke:(namedArguments?:NamedArguments) => Promise<any>)
    {
    }

    public getType():SheetType
    {
        return SheetType.FormulaExpression;
    }

    public toString():string
    {
        return `FormulaExpression`;
    }

    @bind
    public async eval(namedArguments:NamedArguments):Promise<any>
    {
        return await this._invoke(namedArguments);
    }
}

export class SheetContextImpl implements ISheetContext
{
    public constructor(private _sheetIO:ISheetIOContext,
                       private _cellIO:ICellIOContext,
                       private _functionResolver:IFunctionResolver,
                       private _surfaceContext:ISurfaceContext,
                       private _stack:EvalStack)
    {
    }

    public isArrayFormula():boolean
    {
        return this._stack.isArrayFormula();
    }

    public getRangeIterator(range:ISheetRange):ISheetRangeIterator
    {
        return new SheetRangeIteratorImpl(this, range.sheetId, range.from, range.to);
    }

    public reEvaluateLazyCell(location:ISheetLocation):Promise<IsCellReEvaluationDone>
    {
        const eventFrame:IFrame = this._cellIO.getEventFrame();

        return this._sheetIO.reEvaluateLazySlot(location, eventFrame);
    }

    public isLiveCell():boolean
    {
        return this._cellIO.isSlotLive();
    }

    public startLiveCell(location:ISheetLocation):Promise<boolean>
    {
        return this._sheetIO.startLiveSlot(location);
    }

    public async stopLiveCell(location:ISheetLocation):Promise<boolean>
    {
        return this._sheetIO.stopLiveSlot(location);
    }

    public async restartLiveCell(location:ISheetLocation):Promise<boolean>
    {
        await this.stopLiveCell(location);

        return await this.startLiveCell(location);
    }

    public getEventFrame():IFrame
    {
        return this._cellIO.getEventFrame();
    }

    public getCellValue(location:ISheetLocation):IValue
    {
        const cellRef = new CellReference(location.sheetId, location.columnIndex, location.rowIndex);

        this._cellIO.addDependency(cellRef, SlotDependencyType.Value);

        return this._sheetIO.getSlotValue(location, false);
    }

    public getCellStatus(location:ISheetLocation):IValue
    {
        const cellRef = new CellReference(location.sheetId, location.columnIndex, location.rowIndex);

        this._cellIO.addDependency(cellRef, SlotDependencyType.Status);

        return this._sheetIO.getSlotStatus(location);
    }

    public isCellLoading(location:ISheetLocation):boolean
    {
        return this.getCellStatus(location).toString() === SlotStatus.Loading.toString();
    }

    public isCellStreaming(location:ISheetLocation):boolean
    {
        return this.getCellStatus(location).toString() === SlotStatus.Streaming.toString();
    }

    /**
     * Function to convert provided range to Frame
     */
    getRangeValue(range:ISheetRange, autoRows?:boolean, autoCols?:boolean):IFrame
    {
        const sheetId = range.sheetId;
        const computeAutoLength = (rowStart:number, colStart:number, deltaRow:number, deltaCol:number):number =>
        {
            let autoLength = 0;

            let currRow = rowStart;
            let currCol = colStart;
            while( true )
            {
                const cellRef = new CellReference(range.sheetId, currCol, currRow);

                this._cellIO.addDependency(cellRef, SlotDependencyType.Value);

                if( !this._sheetIO.isSlotNonEmpty(cellRef.toSheetLocation()) )
                    break;

                currCol += deltaCol;
                currRow += deltaRow;
                autoLength++;
            }

            return autoLength;
        };

        const fromRowIndex = Math.min(range.from.rowIndex, range.to.rowIndex);
        const fromColIndex = Math.min(range.from.columnIndex, range.to.columnIndex);

        const toRowIndex = autoRows
            ? fromRowIndex + computeAutoLength(fromRowIndex, fromColIndex, 1, 0) - 1
            : Math.max(range.from.rowIndex, range.to.rowIndex);

        const toColIndex = autoCols
            ? fromColIndex + computeAutoLength(fromRowIndex, fromColIndex, 0, 1) - 1
            : Math.max(range.from.columnIndex, range.to.columnIndex);

        const normalizedRange = new SheetRangeImpl(
            sheetId,
            new SheetLocationImpl(sheetId, fromColIndex, fromRowIndex),
            new SheetLocationImpl(sheetId, toColIndex, toRowIndex)
        );

        const rangeValues = this.getRangeValues(normalizedRange);

        const fields = _times(toColIndex - fromColIndex + 1, (index) =>
            new FrameField(`column${index + 1}`, Type.String));

        const values:IValue[][] = [];
        for( let rowIndex = fromRowIndex; rowIndex <= toRowIndex; rowIndex++ )
        {
            const rowValue = [];
            for( let colIndex = fromColIndex; colIndex <= toColIndex; colIndex++ )
            {
                const cellRef = new CellReference(range.from.sheetId, colIndex, rowIndex);

                this._cellIO.addDependency(cellRef, SlotDependencyType.Value);

                rowValue.push(
                    rangeValues.get(cellRef.toSheetLocation().asKey()) || AEmpty.Value
                );
            }

            values.push(rowValue);
        }

        return Frames.fromFieldsAndData(fields, values);
    }

    public getRangeValues(range:ISheetRange):Map<SheetLocationKey, IValue>
    {
        return this._sheetIO.getRangeValues(range);
    }

    public async setCellFormulasInBulk(locations:ISheetLocation[], newValues:IValue[]):Promise<IValue>
    {
        if( !locations || !newValues || locations.length !== newValues.length )
            return ABoolean.false();

        if( locations.length > 0 )
            await this._sheetIO.setSlotFormulasInBulk(locations, newValues);

        return ABoolean.true();
    }

    @bind
    public async setCellFormula(location:ISheetLocation, newValue:IValue):Promise<IValue>
    {
        await this._sheetIO.setSlotFormula(location, newValue);

        return ABoolean.true();
    }

    @action.bound
    getCellFormulaText(location:ISheetLocation, formulaKind:FormulaKind):string | null
    {
        return this._getCellFormulaText(location, formulaKind);
    }

    @bind
    private _getCellFormulaText(location:ISheetLocation, formulaKind:FormulaKind):string | null
    {
        this._cellIO.addDependency(new CellReference(location.sheetId, location.columnIndex, location.rowIndex), SlotDependencyType.Value);

        const slotFormula = this._sheetIO.getSlotFormula(location);

        if( Type.isInstanceOfIValue(slotFormula) )
            return slotFormula.toString();

        const formulaText = slotFormula as string;
        if( !formulaText )
            return '';

        if( formulaKind.isEqualTo(FormulaKind.A1()) )
        {
            const formulaA1 = this._sheetIO.convertR1C1toA1(location, formulaText) as string;
            return formulaA1 ? formulaA1 : '';
        }

        return formulaText;
    }

    @action.bound
    setCellValue(location:ISheetLocation, newValue:IValue):IValue
    {
        this._sheetIO.setSlotValue(location, newValue);

        return ABoolean.true();
    }

    findFirstBlankRowBelow(location:ISheetLocation):ISheetLocation
    {
        let x:ColumnIndex = location.columnIndex;
        let y:RowIndex = location.rowIndex;

        /*Look down from starting point until we find empty cell - this will be considered as empty row*/
        while( this._sheetIO.isSlotNonEmpty(new SheetLocationImpl(location.sheetId, x, y)) )
            y++;

        return new SheetLocationImpl(location.sheetId, x, y);
    }

    @action.bound
    public async addRowsBelow(location:ISheetLocation, values:IFrame):Promise<void>
    {
        const records:Array<IFrameRecord> = values.getRecords();
        const fieldNames:Array<string> = values.getFieldNames();

        let columnIndex:ColumnIndex = location.columnIndex;

        const firstBlankRow = this.findFirstBlankRowBelow(location);
        let rowIndex:RowIndex = firstBlankRow.rowIndex;

        for( let record of records )
        {
            if( !this._sheetIO.isRowExists(location.sheetId, rowIndex) )
                this._sheetIO.insertRows(
                    location.sheetId,
                    this._sheetIO.getRowsCount(location.sheetId),
                    1
                );

            for( let fieldName of fieldNames )
            {
                if( !this._sheetIO.isColExists(location.sheetId, columnIndex) )
                    this._sheetIO.insertColumns(
                        location.sheetId,
                        this._sheetIO.getColumnsCount(location.sheetId),
                        1
                    );

                const cell:IValue = record.getCell(fieldName, false);

                await this._sheetIO.setSlotFormula(new SheetLocationImpl(location.sheetId, columnIndex, rowIndex), cell);

                columnIndex++;
            }

            columnIndex = location.columnIndex;
            rowIndex++;
        }
    }

    @bind
    public readValuesUntilEmptyCell(startAt:ISheetLocation):IFrame
    {
        const rows:IValue[][] = [];

        while( this._sheetIO.isSlotNonEmpty(startAt) )
        {
            const cellValue = this.getCellValue(startAt);

            if( cellValue )
                rows.push([cellValue]);
            else
                rows.push([AEmpty.Value]);

            startAt = new SheetLocationImpl(startAt.sheetId, startAt.columnIndex, startAt.rowIndex + 1);
        }

        return Frames.fromFieldsAndData([new FrameField('values', Type.String)], rows);
    }

    @action.bound
    defineRangeBoundary(startAt:ISheetRange, offset?:number, numItems?:number, columnToUse?:number):ISheetRange | IValue
    {
        let fromRowIndex = Math.min(startAt.from.rowIndex, startAt.to.rowIndex);
        let toRowIndex = fromRowIndex;
        let fromColIndex = Math.min(startAt.from.columnIndex, startAt.to.columnIndex);
        let toColIndex = Math.max(startAt.from.columnIndex, startAt.to.columnIndex);

        let rangeIsEmpty = true;

        while( _range(fromColIndex, toColIndex + 1).some((columnIndex:number) =>
        {
            const sheetLocation:ISheetLocation = new SheetLocationImpl(startAt.sheetId, columnIndex, toRowIndex);

            // do not remove !!! need for dependency subscription
            this.getCellValue(sheetLocation);

            const isCellNonEmpty = this._sheetIO.isSlotNonEmpty(sheetLocation);

            if( isCellNonEmpty ) rangeIsEmpty = false;

            return isCellNonEmpty;
        }) )
        {
            toRowIndex++;
        }

        if( rangeIsEmpty ) return AFrame.empty();

        if( toRowIndex !== fromRowIndex )
            toRowIndex--;

        if( offset === null )
        {
            return new SheetRangeImpl(
                startAt.sheetId,
                new SheetLocationImpl(startAt.sheetId, fromColIndex, fromRowIndex),
                new SheetLocationImpl(startAt.sheetId, toColIndex, toRowIndex)
            );
        }

        if( offset === 0 || offset < 0 ) return AFrame.empty();

        if( columnToUse === 0 || columnToUse < 0 ) return AFrame.empty();

        if( columnToUse === null ) columnToUse = 1;

        const columnToUseIndex = fromColIndex + columnToUse - 1;

        if( columnToUseIndex > toColIndex ) return AFrame.empty();

        if( fromRowIndex + offset - 1 > toRowIndex ) return AFrame.empty();

        fromRowIndex += offset - 1;

        if( numItems !== null )
        {
            if( numItems === 0 || numItems < 0 ) return AFrame.empty();

            if( fromRowIndex + numItems - 1 < toRowIndex ) toRowIndex = fromRowIndex + numItems - 1;
        }

        // if 2 or 3 or 4 arguments passed in, returns always 1 column.
        fromColIndex = toColIndex = columnToUseIndex;

        return new SheetRangeImpl(
            startAt.sheetId,
            new SheetLocationImpl(startAt.sheetId, fromColIndex, fromRowIndex),
            new SheetLocationImpl(startAt.sheetId, toColIndex, toRowIndex)
        );
    }

    @bind
    public clearRange(fromCellInclusive:ISheetLocation, toCellInclusive:ISheetLocation, keepBindings:boolean = false):Promise<void>
    {
        return this._sheetIO.clearSlotsRange(fromCellInclusive, toCellInclusive, keepBindings);
    }

    @bind
    public deleteRows(fromLoc:ISheetLocation, toLoc:ISheetLocation, keepBindings:boolean):Promise<void>
    {
        return this._sheetIO.deleteRows(fromLoc, toLoc, keepBindings);
    }

    @bind
    public deleteColumns(fromLoc:ISheetLocation, toLoc:ISheetLocation, keepBindings:boolean):Promise<void>
    {
        return this._sheetIO.deleteColumns(fromLoc, toLoc, keepBindings);
    }

    @action.bound
    insertFrameRows(targetLocation:ISheetLocation, insertIndex:number, frameToAdd:IFrame, addAll:boolean):ABoolean | AError
    {
        return this._changeFrameAtLocation(targetLocation,
            frame => this._insertFrameRows(frame, insertIndex, frameToAdd, addAll),
            (oldFrame, newFrame, pathInFrameHead) =>
            {
                // don't change at all to make it consistent with Repeater
                return BindingOpResult.SKIP;

                // const deltaIndex = newFrame.recordCount - oldFrame.recordCount;
                // if( deltaIndex == 0 || pathInFrameHead.rowIndex < insertIndex ) return BindingOpResult.SKIP;
                //
                // return BindingOpResult.change(pathInFrameHead.withRowIndex(pathInFrameHead.rowIndex + deltaIndex));
            }
        );
    }

    @action.bound
    updateFrameRow(targetLocation:ISheetLocation, updateIndex:number, updateFrame:IFrame):ABoolean | AError
    {
        return this._changeFrameAtLocation(targetLocation, frame =>
            this._updateFrameRow(frame, updateIndex, updateFrame)
        );
    }

    @action.bound
    deleteFrameRows(targetLocation:ISheetLocation, deleteIndexes:Array<number>, keepBindings:boolean):ABoolean | AError
    {
        return this._changeFrameAtLocation(targetLocation,
            frame => this._deleteFrameRows(frame, deleteIndexes),
            (oldFrame, newFrame, pathInFrameHead) =>
            {
                // don't change at all to make it consistent with Repeater
                return BindingOpResult.SKIP;

                // //count indexes less then current - this gives us delta to change the current index
                // let deltaIndex = _sumBy(deleteIndexes, deleteIndex => deleteIndex < pathInFrameHead.rowIndex ? 1 : 0);
                //
                // let deleteBinding = !keepBindings && deleteIndexes.indexOf(pathInFrameHead.rowIndex) >= 0;
                //
                // if( deleteBinding )
                //     return BindingOpResult.DELETE;
                //
                // return deltaIndex == 0
                //     ? BindingOpResult.SKIP
                //     : BindingOpResult.change(pathInFrameHead.withRowIndex(pathInFrameHead.rowIndex - deltaIndex));
            }
        );
    }

    @action.bound
    insertFrameColumns(targetLocation:ISheetLocation, insertIndex:number, columnName:string, columnType:Type):ABoolean | AError
    {
        return this._changeFrameAtLocation(targetLocation, frame =>
            this._insertFrameColumn(frame, insertIndex, columnName, columnType)
        );
    }

    @action.bound
    updateFrameColumn(targetLocation:ISheetLocation, updateIndex:number, newColumnName:string, newColumnType?:Type):ABoolean | AError
    {
        let oldColumnName = null;
        return this._changeFrameAtLocation(targetLocation,
            frame =>
            {
                const updateResult = this._updateFrameColumn(frame, updateIndex, newColumnName, newColumnType);
                oldColumnName = frame.getFields()[updateIndex].name;
                return updateResult;
            },
            (oldFrame, newFrame, pathInFrameHead) =>
            {
                return pathInFrameHead.fieldName === oldColumnName
                    ? BindingOpResult.change(pathInFrameHead.withFieldName(newColumnName))
                    : BindingOpResult.SKIP;
            }
        );
    }

    @action.bound
    deleteFrameColumns(targetLocation:ISheetLocation, deleteIndexes:Array<number>):ABoolean | AError
    {
        let columnNamesToDeleteMap = {};
        return this._changeFrameAtLocation(targetLocation,
            frame =>
            {
                const fields = frame.getFields();
                columnNamesToDeleteMap = ArrayUtils.arrayToMap(deleteIndexes, deleteIndex => fields[deleteIndex] && fields[deleteIndex].name);

                return this._deleteFrameColumns(frame, deleteIndexes);
            },
            (oldFrame, newFrame, pathInFrameHead) => columnNamesToDeleteMap[pathInFrameHead.fieldName]
                ? BindingOpResult.DELETE
                : BindingOpResult.SKIP
        );
    }

    @action.bound
    public async copyRangeWithFormulas(range:ISheetRange, to:ISheetLocation, times:number = 1):Promise<void>
    {
        let columnIndex:ColumnIndex = to.columnIndex;
        let rowIndex:RowIndex = to.rowIndex;

        /*Look down from starting point until we find empty cell - this will be considered as empty row*/
        while( this._sheetIO.isSlotNonEmpty(new SheetLocationImpl(to.sheetId, columnIndex, rowIndex)) )
        {
            rowIndex++;
        }

        const fromColumnIndex = Math.min(range.from.columnIndex, range.to.columnIndex);
        const fromRowIndex = Math.min(range.from.rowIndex, range.to.rowIndex);

        const toColumnIndex = Math.max(range.from.columnIndex, range.to.columnIndex);
        const toRowIndex = Math.max(range.from.rowIndex, range.to.rowIndex);

        for( let i = 0; i < times; i++ )
        {
            for( let currentRowIndex = fromRowIndex; currentRowIndex <= toRowIndex; currentRowIndex++ )
            {
                if( !this._sheetIO.isRowExists(to.sheetId, rowIndex) )
                    await this._sheetIO.insertRows(to.sheetId, this._sheetIO.getRowsCount(to.sheetId), 1);

                for( let currentColIndex = fromColumnIndex; currentColIndex <= toColumnIndex; currentColIndex++ )
                {
                    if( !this._sheetIO.isColExists(to.sheetId, columnIndex) )
                        await this._sheetIO.insertColumns(to.sheetId, this._sheetIO.getColumnsCount(to.sheetId), 1);

                    const slotLocation:ISheetLocation = new SheetLocationImpl(
                        to.sheetId,
                        currentColIndex,
                        currentRowIndex
                    );
                    const slotFormula:SlotFormula = this._sheetIO.getSlotFormula(
                        slotLocation
                    );

                    if( slotFormula )
                    {
                        const newSlotLocation:ISheetLocation = new SheetLocationImpl(
                            to.sheetId,
                            columnIndex,
                            rowIndex
                        );

                        await this._sheetIO.setSlotFormula(newSlotLocation, slotFormula);

                        if( this._sheetIO.isSlotLazy(slotLocation) )
                        {
                            const slotValue:IValue = this._sheetIO.getSlotValue(slotLocation, false);

                            await this._sheetIO.setSlotValue(newSlotLocation, slotValue);
                        }
                    }
                    columnIndex++;
                }

                columnIndex = to.columnIndex;
                rowIndex++;
            }
        }
    }

    public collapseRange(range:ISheetRange, direction:SheetDirection, side:SheetRangeSide, amount:number):void
    {

    }

    public expandRange(range:ISheetRange, direction:SheetDirection, side:SheetRangeSide, amount:number):void
    {

    }

    public getSlotId():SlotId
    {
        return this._cellIO.getSlotId();
    }

    public getSheetId():SheetId
    {
        return this._cellIO.getSlotSheetId();
    }

    public getCellLocation():ISheetLocation
    {
        const slotSheetId = this._cellIO.getSlotSheetId();
        const slotLocation = this._cellIO.getSlotLocation();
        return new SheetLocationImpl(slotSheetId, slotLocation.colIndex, slotLocation.rowIndex);
    }

    public getCellCurrentValue():IValue
    {
        const slotSheetId = this._cellIO.getSlotSheetId();
        const slotLocation = this._cellIO.getSlotLocation();

        return this._sheetIO.getSlotValue(
            new SheetLocationImpl(
                slotSheetId,
                slotLocation.colIndex,
                slotLocation.rowIndex
            ),
            true
        );
    }

    public convertToIValue(value:IValue | ISheetValue):IValue | IValue[]
    {
        return FormulaValueCoercionUtils.ConvertToIValue(this, new SimpleNamingContext(), value);
    }

    public findCellLocationWithSheetName(columnIndex:number, rowIndex:number, sheetName?:string):ISheetLocation | Empty
    {
        return this._sheetIO.findCellLocationWithSheetName(
            this.getSheetId(),
            columnIndex,
            rowIndex,
            sheetName
        );
    }

    public findCellLocationWithSheetId(columnIndex:number, rowIndex:number, sheetId?:SheetId):ISheetLocation | Empty
    {
        return this._sheetIO.findCellLocationWithSheetId(
            this.getSheetId(),
            columnIndex,
            rowIndex,
            sheetId
        );
    }

    evalExpr(exprText:string, namedArguments:NamedArguments, useCurrentContext:boolean):Promise<IValue>
    {
        const parseFn = (evalText:string, slotLocation:SlotLocation) =>
        {
            const parseContext:IFormulaParseContext = {
                workbookId: this._cellIO.getSlotWorkbookId(),
                sheetId: this._cellIO.getSlotSheetId(),
                colIndex: slotLocation.colIndex,
                rowIndex: slotLocation.rowIndex,
                findSheetIdByName: this._sheetIO.findSheetByName
            };
            return FormulaEngine.getInstance().parseExpr(parseContext, evalText);
        };

        return this._parseTextThenEvaluate(exprText, namedArguments, useCurrentContext, parseFn);
    }

    public evalFormula(formulaText:string, formulaKind:FormulaKind, namedArguments:NamedArguments,
                       useCurrentContext:boolean):Promise<IValue>
    {
        try
        {
            const parseFn = (evalText:string, slotLocation:SlotLocation) =>
            {
                let parseResult:OhmParseResult;

                if( this._sheetIO.isSheetExists(this._cellIO.getSlotSheetId()) )
                {
                    const parseContext:IFormulaParseContext = {
                        workbookId: this._cellIO.getSlotWorkbookId(),
                        sheetId: this._cellIO.getSlotSheetId(),
                        colIndex: slotLocation.colIndex,
                        rowIndex: slotLocation.rowIndex,
                        findSheetIdByName: this._sheetIO.findSheetByName
                    };

                    parseResult = FormulaKind.A1().isEqualTo(formulaKind)
                        ? FormulaEngine.getInstance().parseA1(parseContext, evalText)
                        : FormulaEngine.getInstance().parseRC(parseContext, evalText);
                }
                else
                    parseResult = new OhmParseResult(
                        false,
                        null,
                        FormulaType.RegularFormula,
                        `InternalError: could not find sheet with id: ${this._cellIO.getSlotSheetId()}`
                    );
                return parseResult;
            };
            return this._parseTextThenEvaluate(formulaText, namedArguments, useCurrentContext, parseFn);
        }
        catch( e )
        {
            console.error(e);
            mk.error(area.FormulaEval, e);
        }

        return Promise.resolve(EvalErrors.Error);
    }

    public getLocationHash():string
    {
        this._cellIO.addReadLocationHashDependency();

        return this._sheetIO.getLocationHash();
    }

    public getViewportInfoFrame():IFrame
    {
        const {surfaceArea} = commonControllers;
        const surfaceState:SurfaceState = surfaceArea.getState();

        this._cellIO.addReadViewportInfoDependency();

        if( !surfaceState )
            return null;

        const {viewportSize} = surfaceState;

        const viewportInfo:any = {
            width: viewportSize.width.value,
            height: viewportSize.height.value,
            type: ViewportUtil.getViewportTypeByWidth(viewportSize.width.value)
        };

        return JsonUtil.convertJsonObjectToFrame(viewportInfo);
    }

    private _changeFrameAtLocation(targetLocation:ISheetLocation, frameOp:FrameOp, bindingOp?:BindingOp):ABoolean | AError
    {
        const error = new AError('#TARGET_IS_NOT_A_FRAME');

        const cellRef = new CellReference(targetLocation.sheetId, targetLocation.columnIndex, targetLocation.rowIndex);

        this._cellIO.addDependency(cellRef, SlotDependencyType.Value);

        const targetValue = this._sheetIO.getSlotValue(targetLocation, false);
        if( !targetValue )
            return error;

        if( !Type.isInstanceOf(targetValue, Type.Frame) )
            return error;

        const targetFrame = targetValue as IFrame;

        const newFrameOrError = frameOp(targetFrame);
        if( Type.isInstanceOf(newFrameOrError, Type.Error) )
            return newFrameOrError as AError;

        const newFrame = newFrameOrError as IFrame;
        if( !newFrame )
            return ABoolean.false();

        /* if( bindingOp )
            this._updateSlotFrameBindings(targetSlot, targetFrame, newFrame, bindingOp); */ //TODO: Should be reimplemented when binding context is available (in scope of bindings for cloud sheets)

        this.setCellFormula(targetLocation, newFrame);

        return ABoolean.true();
    }

    private _updateSlotFrameBindings(slot:Slot, oldFrame:IFrame, newFrame:IFrame, bindingOp:BindingOp)
    {
        /*
        const runtimeState = bindingsController.getRuntimeState();

        const bindingsToRemove = {};
        const bindingsToChange = {};
        this._sheetIO.findBindingsForSlot(slot).forEach(binding =>
        {
            let bindingChanged = false;
            const newBinding = doWithBinding<IBinding>(binding,
                bScalar =>
                {
                    if( !bScalar.pathInFrame || bScalar.pathInFrame.length == 0 )
                        return bScalar;

                    const bindingOpResult = bindingOp(oldFrame, newFrame, bScalar.pathInFrame[0]);
                    if( bindingOpResult === BindingOpResult.SKIP )
                        return bScalar;

                    if( bindingOpResult === BindingOpResult.DELETE )
                    {
                        bindingsToRemove[binding.id] = true;
                        return bScalar;
                    }

                    const newPathInFrame = [bindingOpResult.newPathInFrameHead, ...bScalar.pathInFrame.slice(1)];

                    bindingChanged = true;
                    return bScalar.copyWithPathInFrameRetainingId(newPathInFrame);
                },
                bArray =>
                {
                    const newVectors = bArray.vectors.map(vector =>
                    {
                        if( !vector.pathInFrame || vector.pathInFrame.length == 0 )
                            return vector;

                        const bindingOpResult = bindingOp(oldFrame, newFrame, vector.pathInFrame[0]);
                        if( bindingOpResult === BindingOpResult.SKIP )
                            return vector;

                        if( bindingOpResult === BindingOpResult.DELETE )
                        {
                            bindingsToRemove[binding.id] = true;
                            return vector;
                        }

                        const newPathInFrame = [bindingOpResult.newPathInFrameHead, ...vector.pathInFrame.slice(1)];

                        bindingChanged = true;
                        return vector.copyWithPathInFrame(newPathInFrame);
                    });

                    return bArray.copyWithVectors(newVectors);
                }
            );

            if( bindingChanged )
                runtimeState.setBinding(newBinding);
        });

        Object.keys(bindingsToRemove).forEach(bindingId =>
            this._sheetIO.bindingsController.removeBinding(bindingId)
        ); */ //TODO: Should be reimplemented when binding context is available (in scope of bindings for cloud sheets)
    }

    private _insertFrameRows(frame:IFrame, insertIndex:number, frameToAdd:IFrame, addAll:boolean):IFrame
    {
        Assert.yes(insertIndex >= 0 && insertIndex <= frame.recordCount, 'insertIndex out of bounds');

        const fields = frame.getFields();
        const fieldsCnt = fields.length;
        const fieldsMap = ArrayUtils.arrayToMap(fields, f => f.name);
        const records = frame.getRecords();

        const addFields = frameToAdd.getFields();
        const addFieldsCnt = addFields.length;
        const addFieldsToIndexMap = ArrayUtils.arrayToMap(addFields, f => f.name, (f, index) => index);
        const addRecords = addAll
            ? frameToAdd.getRecords()
            : frameToAdd.getRecords().slice(0, 1); // ! this also works for an empty array
        const isAddingAnonymousFrame = addFields.every(f => !!f.name.match(AUTO_COLUMN_NAME_RE));

        // populate new fields
        const resultFields = [...fields];
        if( isAddingAnonymousFrame )
        {
            // just add extra fields to the end
            let nameIndex = (resultFields.length + 1);
            while( addFields.length > resultFields.length )
            {
                // find auto name, that is not yet present
                let fieldName;
                do
                {
                    fieldName = 'column' + (nameIndex++);
                }
                while( fieldsMap[fieldName] );

                const fieldIndex = resultFields.length;
                resultFields.push(new FrameField(fieldName, addFields[fieldIndex].type));
            }
        }
        else
        {
            // add only fields, that aren't yet present
            resultFields.push(
                ...addFields.filter(f => !fieldsMap[f.name])
            );
        }

        const resultFieldsCnt = resultFields.length;

        // first, fill existing records with #empty values for new columns
        const data = records.map(record => [
            ...record.getCells(),
            ...AEmpty.array(resultFieldsCnt - fieldsCnt)
        ]);

        // second, add new records
        const addData = addRecords.map(record =>
        {
            const cells = record.getCells();
            return resultFields.map((field, fieldIndex) =>
            {
                // cells[-1] and cells[undefined] will both just return `undefined`,
                //  so below code will work as expected
                //  for both "index-gt-cells-length" and "undefined-index-caused-by-unexisting-field-name-in-a-map"
                const value = isAddingAnonymousFrame
                    ? cells[fieldIndex]
                    : cells[addFieldsToIndexMap[field.name]];

                return value || AEmpty.Value;
            });
        });

        const resultData = [
            ...data.slice(0, insertIndex),
            ...addData,
            ...data.slice(insertIndex)
        ];
        return Frames.fromFieldsAndData(resultFields, resultData, frame.getMeta());
    }

    private _updateFrameRow(frame:IFrame, updateIndex:number, updateFrame:IFrame):IFrame | AError
    {
        Assert.yes(updateIndex >= 0 && updateIndex < frame.recordCount, 'updateIndex out of bounds');

        if( updateFrame.recordCount == 0 )
            return new AError('#UPDATING_FRAME_IS_EMPTY');

        const fields = frame.getFields();
        const records = frame.getRecords();

        const updateFields = updateFrame.getFields();
        const updateFieldsToIndexMap = ArrayUtils.arrayToMap(updateFields, f => f.name, (f, index) => index);
        const updateCells = updateFrame.getRecords()[0].getCells();
        const isUpdatingWithAnonymousFrame = updateFields.every(f => !!f.name.match(AUTO_COLUMN_NAME_RE));

        const data = records.map((record, recordIndex) =>
        {
            const cells = record.getCells();

            // just clone non-updating records
            if( recordIndex !== updateIndex )
                return cells;

            const row = [];
            for( let i = 0; i < fields.length; ++i )
            {
                const field = fields[i];

                let cell;
                if( isUpdatingWithAnonymousFrame )
                    cell = i < updateCells.length ? updateCells[i] : cells[i];
                else
                {
                    // updateCells[undefined] will just return `undefined`,
                    // so below code will work as expected in case we don't have field in updatingFrame
                    cell = updateCells[updateFieldsToIndexMap[field.name]] || cells[i];
                }

                row.push(cell);
            }

            return row;
        });

        return Frames.fromFieldsAndData(fields, data, frame.getMeta());
    }

    private _deleteFrameRows(frame:IFrame, deleteIndexes:Array<number>):IFrame
    {
        //note: this method assumes deleteIndexes array to be sorted ascendingly
        //  also that deleteIndexes are in bounds of frame's recordCount

        const fields = frame.getFields();
        const records = frame.getRecords();
        const recordCount = records.length;

        const data = [];
        let recordIndex = 0;

        // add out-of-bounds deleteIndex to make the loop add all ending records
        for( let deleteIndex of deleteIndexes.concat([recordCount + 1]) )
        {
            // add all records until current deleteIndex
            while( recordIndex < deleteIndex && recordIndex < recordCount )
                data.push(records[recordIndex++].getCells());

            //skip deleteIndex'ed record
            ++recordIndex;
        }

        return Frames.fromFieldsAndData(fields, data, frame.getMeta());
    }

    private _insertFrameColumn(frame:IFrame, insertIndex:number, columnName:string, columnType:Type):IFrame | AError
    {
        Assert.yes(insertIndex >= 0 && insertIndex <= frame.fieldCount, 'insertIndex out of bounds');

        if( frame.findField(columnName) )
            return new AError('#COLUMN_EXISTS');

        const newColumn = new AFrameColumn(columnName, columnType, []);

        return frame.withColumns([newColumn], insertIndex);
    }

    private _updateFrameColumn(frame:IFrame, updateIndex:number, newColumnName:string, newColumnType?:Type):IFrame | AError
    {
        Assert.yes(updateIndex >= 0 && updateIndex < frame.fieldCount, 'updateIndex out of bounds');

        const records = frame.getRecords();

        const fields = frame.getFields();
        fields[updateIndex] = new FrameField(newColumnName, newColumnType || fields[updateIndex].type);

        const data = [];
        for( let record of records )
        {
            const row = [];

            const cells = record.getCells();
            for( let cellIndex = 0; cellIndex < cells.length; ++cellIndex )
            {
                let cell = cells[cellIndex];

                if( newColumnType && cellIndex === updateIndex )
                {
                    const coerced = CoercionUtils.coerce(cell, newColumnType, null);
                    if( Type.isInstanceOf(coerced, Type.Error) )
                        return coerced as AError;

                    cell = coerced as IValue;
                }

                row.push(cell);
            }
            data.push(row);
        }

        return Frames.fromFieldsAndData(fields, data, frame.getMeta());
    }

    private _deleteFrameColumns(frame:IFrame, deleteIndexes:Array<number>):IFrame
    {
        Assert.yes(
            deleteIndexes.every(deleteIndex => deleteIndex >= 0 && deleteIndex < frame.fieldCount),
            'deleteIndex out of bounds'
        );

        return frame.withoutColumns(deleteIndexes);
    }

    public isSheetExists(sheetId:SheetId):boolean
    {
        return this._sheetIO.isSheetExists(sheetId);
    }

    public getRowsCount(sheetId:SheetId):number
    {
        return this._sheetIO.getRowsCount(sheetId);
    }

    public getColumnsCount(sheetId:SheetId):number
    {
        return this._sheetIO.getColumnsCount(sheetId);
    }

    public getWorkbookId(sheetId:SheetId):WorkbookId
    {
        return this._sheetIO.getWorkbookId(sheetId);
    }

    public getCellSettings(location:ISheetLocation):ISheetCellSettings
    {
        return this._sheetIO.getCellSettings(location);
    }

    public getCellFormat(location:ISheetLocation):ISheetCellFormat
    {
        return this._sheetIO.getCellFormat(location);
    }

    public setCellSettings(location:ISheetLocation, newCellSettings:ISheetCellSettings):void
    {
        this._sheetIO.setCellSettings(location, newCellSettings);
    }

    public setCellFormat(location:ISheetLocation, newCellFormatId:string):void
    {
        this._sheetIO.setCellFormat(location, newCellFormatId);
    }

    public getRowWidth(location:ISheetLocation):number
    {
        return this._sheetIO.getRowWidth(location);
    }

    public getDesignName():string
    {
        return this._sheetIO.getDesignName();
    }

    private _parseTextThenEvaluate(exprText:string, namedArguments:NamedArguments, useCurrentContext:boolean,
                                   parseFn:(evalText:string, slotLocation:SlotLocation) => OhmParseResult)
    {
        const slotLocation:SlotLocation = this._cellIO.getSlotLocation();
        const parseResult = parseFn(exprText, slotLocation);
        if( parseResult && parseResult.isParseSuccessful )
        {
            const evalStack = new EvalStack();
            const cellIOContext = useCurrentContext ? this._cellIO : this._cellIO.clone();

            return FormulaEngine.getInstance()
                .evalFormulaWithContext(
                    parseResult.rootNode,
                    evalStack,
                    this._sheetIO,
                    cellIOContext,
                    this._functionResolver,
                    this._surfaceContext,
                    namedArguments
                )
                .then(r => r.value)
                .catch(reason =>
                {
                    console.error(reason);
                    mk.error(area.FormulaEval, reason);
                    return EvalErrors.Error;
                });
        }
        else
        {
            mk.error(area.FormulaEval, parseResult.errorMessage);
            return Promise.resolve(EvalErrors.InvalidFormula);
        }
    }
}
